import React, { Suspense } from 'react';
import { ErrorBoundary } from '@sparknz/journeys-common';
import { ActionLayer, Box } from '@sparknz/spark-pattern-library';

import ContentLayerLoader from 'shared/components/ContentLayerLoader';
import { useChangePlanActionLayer } from '../../hooks/useChangePlanActionLayer';
import GenericErrorBanner from '../GenericErrorBanner';
import ChangePlanAccountSelectionContent from '../MobilePlanCards/components/ChangePlanAccountSelectionContent';
import ChangePlanLineSelectionContent from '../MobilePlanCards/components/ChangePlanLineSelectionContent';
import ChangePlanParentPlanSelectionContent from '../MobilePlanCards/components/ChangePlanParentPlanSelectionContent';
import { BreadcrumbClickEvent, ViewState } from '../MobilePlanCards/types';
import {
  buildProgressIndicatorSections,
  MinimalSection,
} from '../MobilePlanCards/utils/buildProgressIndicatorSections.utils';

interface ChangePlanActionLayerProps {
  isOpen: boolean;
}

const INITIAL_PROGRESS_VALUE = 10;

const getActionLayerHeaderProps = (
  viewState: ViewState,
  isEligibilityCheckRequired?: boolean,
) => {
  if (isEligibilityCheckRequired) {
    return { title: 'Change plan' };
  }

  switch (viewState) {
    case ViewState.ManageAccount:
      return { title: 'Account selection', subtitle: 'Account' };

    case ViewState.ManageLines:
      return { title: 'Mobile phone plans', subtitle: 'Line' };

    default:
      return { title: '' };
  }
};

const ChangePlanActionLayerContents = ({
  viewState,
}: {
  viewState: ViewState;
}) => {
  switch (viewState) {
    case ViewState.ManageAccount:
      return <ChangePlanAccountSelectionContent />;

    case ViewState.ManageLines:
      return <ChangePlanLineSelectionContent />;

    case ViewState.SelectParentPlan:
      return <ChangePlanParentPlanSelectionContent />;

    default:
      throw new Error('Unsupported change plan action layer step.');
  }
};

const ErrorFallback = () => <GenericErrorBanner />;

const ChangePlanActionLayer = ({ isOpen }: ChangePlanActionLayerProps) => {
  const {
    isEligibilityCheckRequired,
    accounts,
    eligibleLines,
    viewState,
    navigateToAccountSelect,
    navigateToLineSelect,
    closeActionLayer,
  } = useChangePlanActionLayer();

  function handleBreadcrumbClick({ section }: BreadcrumbClickEvent) {
    switch (section.sectionId) {
      case ViewState.ManageAccount:
        navigateToAccountSelect();
        break;

      case ViewState.ManageLines:
        navigateToLineSelect();
        break;

      default:
        throw new Error('Unsupported breadcrumb click event.');
    }
  }

  return (
    <ActionLayer
      {...getActionLayerHeaderProps(viewState, isEligibilityCheckRequired)}
      isOpen={isOpen}
      onClose={closeActionLayer}
      status="default"
      {...(isEligibilityCheckRequired
        ? {
            progressIndicatorProps: {
              activeSectionId: viewState,
              progressionThreshold: INITIAL_PROGRESS_VALUE,
              onClick: handleBreadcrumbClick,
              sections: buildProgressIndicatorSections(
                [
                  (accounts ?? []).length > 1 && {
                    sectionId: ViewState.ManageAccount,
                    name: 'Account',
                  },
                  (eligibleLines ?? []).length > 1 && {
                    sectionId: ViewState.ManageLines,
                    name: 'Line',
                  },
                  { sectionId: ViewState.SelectParentPlan, name: 'Adding' },
                ].filter(Boolean) as MinimalSection[],
              ),
            },
          }
        : {})}
    >
      <Box py="s4">
        <ErrorBoundary fallback={ErrorFallback}>
          <Suspense fallback={<ContentLayerLoader />}>
            <ChangePlanActionLayerContents viewState={viewState} />
          </Suspense>
        </ErrorBoundary>
      </Box>
    </ActionLayer>
  );
};

export default ChangePlanActionLayer;
